import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { UtensilsCrossed, Package, Truck, DollarSign, Eye, Sparkles, ChevronRight } from "lucide-react";

const FleetyLogo = ({ size = "small", className = "" }) => {
  const sizeMap = { small: "h-7", medium: "h-10", large: "h-14" };
  return (
    <img
      src="https://customer-assets.emergentagent.com/job_hungry-fleety/artifacts/oq66dorz_Fleety%20Logo%20zoomed.jpg"
      alt="Fleety"
      className={`${sizeMap[size]} w-auto object-contain ${className}`}
      data-testid="fleety-logo"
    />
  );
};

const FeaturePill = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-1.5 bg-[#EFF8FF] px-3 py-1.5 rounded-full border border-[#3FA9F5]/20">
    <Icon size={12} className="text-[#3FA9F5]" />
    <span className="text-[#0C4A6E] text-xs font-semibold">{text}</span>
  </div>
);

const ServiceCard = ({ icon: Icon, title, description, bgColor, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full px-4 py-4 rounded-2xl ${bgColor} text-white text-left shadow-md relative overflow-hidden active:scale-[0.98] transition-transform`}
    data-testid={`service-${title.toLowerCase().replace(" ", "-")}`}
  >
    <div className="relative z-10 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-white/25 rounded-xl flex items-center justify-center shrink-0">
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <h3 className="font-bold text-sm">{title}</h3>
          <p className="text-white/80 text-xs leading-snug mt-0.5">{description}</p>
        </div>
      </div>
      <ChevronRight size={16} className="text-white/70 shrink-0 ml-2" />
    </div>
    <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-white/10 rounded-full" />
  </button>
);

const LandingPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("fleety_user");
    if (userData) navigate("/home");
  }, [navigate]);

  return (
    <div
      className="flex flex-col bg-white"
      style={{ height: "100dvh", overflow: "hidden" }}
      data-testid="landing-page"
    >
      {/* Top accent */}
      <div className="h-1 bg-[#3FA9F5] shrink-0" />

      {/* Header */}
      <header className="flex justify-between items-center px-5 py-3 shrink-0">
        <FleetyLogo size="small" />
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate("/login")}
            className="text-gray-600 hover:text-[#3FA9F5] font-medium text-sm transition-colors px-3 py-1.5"
            data-testid="login-link"
          >
            Login
          </button>
          <Button
            onClick={() => navigate("/signup")}
            className="bg-[#3FA9F5] hover:bg-[#2196F3] text-white text-sm font-semibold px-4 h-8 rounded-full shadow-fleety"
            data-testid="signup-btn"
          >
            Sign Up
          </Button>
        </div>
      </header>

      {/* Main — grows to fill remaining height */}
      <main className="flex-1 flex flex-col justify-between px-5 py-2 min-h-0">

        {/* Tagline */}
        <div>
          <h1 className="font-heading text-xl font-bold text-gray-900 leading-tight mb-1">
            Swift. Transparent. Yours.
          </h1>
          <p className="text-gray-500 text-xs leading-relaxed">
            Order food or send packages across Bangalore with Fleety's fast & reliable service.
          </p>
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap gap-1.5">
          <FeaturePill icon={Truck} text="Fast Delivery" />
          <FeaturePill icon={DollarSign} text="No hidden fees" />
          <FeaturePill icon={Sparkles} text="No inflated prices" />
          <FeaturePill icon={Eye} text="Pure transparency" />
        </div>

        {/* Service area notice */}
        <div className="px-3 py-2 bg-blue-50 rounded-xl border border-blue-100" data-testid="service-area-banner">
          <p className="text-[#0C4A6E] text-xs text-center font-medium">
            🌟 Hey 560075, we're here! ✨
          </p>
          <p className="text-gray-500 text-xs text-center leading-snug mt-0.5">
            Fleety is live and growing — can't wait to bring the love across Namma Bengaluru soon 🫶
          </p>
        </div>

        {/* Service cards */}
        <div className="space-y-2">
          <ServiceCard
            icon={UtensilsCrossed}
            title="Fleety Food"
            description="Order from your favourite restaurants with no hidden fees"
            bgColor="bg-gradient-to-r from-orange-500 to-red-500"
            onClick={() => navigate("/home")}
          />
          <ServiceCard
            icon={Package}
            title="FleetyGo"
            description="Send anything, anywhere in Bangalore — fast & affordable"
            bgColor="bg-gradient-to-r from-[#3FA9F5] to-[#2196F3]"
            onClick={() => navigate("/fleetygo")}
          />
        </div>

        {/* Footer */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 text-gray-400 text-xs">
            <button onClick={() => navigate("/terms")} className="hover:text-[#3FA9F5] transition-colors">Terms</button>
            <span>·</span>
            <button onClick={() => navigate("/faqs")} className="hover:text-[#3FA9F5] transition-colors">FAQs</button>
            <span>·</span>
            <a href="https://www.fleety.co.in" target="_blank" rel="noopener noreferrer" className="hover:text-[#3FA9F5] transition-colors">About Us</a>
          </div>
          <p className="text-gray-300 text-xs mt-1">© 2025 Fleety. All rights reserved.</p>
        </div>

      </main>
    </div>
  );
};

export default LandingPage;
